import { BreadCrumb } from "components/BreadCrumb";
import DataTable, { Column, Row } from "components/DataTable";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import apiService from "services/apiService";
import { sortByKey } from "util/sortArrByName";

interface PayrollRow extends Row {
  id: number;
  empCode: string;
  empName: string;
  branch: string;
  department: string;
  designation: string;
}

interface PayrollRowWithEmail extends PayrollRow {
  email: string;
}

interface UserApiResponse {
  id: number;
  email: string;
  displayName: string;
  employeeId?: string;
  ofcLocation?: string;
  city?: string;
  department?: string;
  designation?: string;
}

const breadCrumbList = [
  { name: "Generate Payroll", link: "/admin/generate-payroll" },
  { name: "Run", link: "#" },
];

const GeneratePayrollRun: React.FC = () => {
  const location = useLocation();
  const selectedEmployeeFromState = (
    location.state as { selectedEmployeeEmail?: string | null } | null
  )?.selectedEmployeeEmail;

  const [fromDate, setFromDate] = useState("2026-03-01");
  const [toDate, setToDate] = useState("2026-03-31");
  const [selectAll, setSelectAll] = useState(false);
  const [rows, setRows] = useState<PayrollRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);

  

  const handleToggleRow = (rowId: number, checked: boolean) => {
    setSelectedRowIds((prev) => {
      if (checked) {
        if (prev.includes(rowId)) {
          return prev;
        }
        return [...prev, rowId];
      }

      return prev.filter((id) => id !== rowId);
    });
  };

  const handleSelectAllChange = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedRowIds(rows.map((row) => row.id));
      return;
    }
    setSelectedRowIds([]);
  };

  useEffect(() => {
    setLoading(true);
    apiService
      .get("api/users")
      .then((response) => {
        const mappedRows: PayrollRowWithEmail[] = (
          response.data as UserApiResponse[]
        ).map((user) => ({
          id: user.id,
          empCode:
            user.employeeId && user.employeeId.trim()
              ? user.employeeId
              : String(user.id),
          empName: user.displayName || "-",
          branch: user.ofcLocation || user.city || "-",
          department: user.department || "-",
          designation: user.designation || "-",
          email: user.email,
        }));

        const filteredBySelected = selectedEmployeeFromState
          ? mappedRows.filter((row) => row.email === selectedEmployeeFromState)
          : mappedRows;

        const payrollRows: PayrollRow[] = filteredBySelected.map((row) => ({
          id: row.id,
          empCode: row.empCode,
          empName: row.empName,
          branch: row.branch,
          department: row.department,
          designation: row.designation,
        }));

        setRows(sortByKey(payrollRows, "empName"));
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedEmployeeFromState]);

  useEffect(() => {
    if (!rows.length) {
      setSelectAll(false);
      setSelectedRowIds([]);
      return;
    }

    const allSelected = rows.every((row) => selectedRowIds.includes(row.id));
    setSelectAll(allSelected);
  }, [rows, selectedRowIds]);

  const tableColumns: Column[] = [
    {
      key: "select",
      label: "",
      excludeExport: true,
      render: (_value, row) => {
        const typedRow = row as PayrollRow;
        const isChecked = selectedRowIds.includes(typedRow.id);

        return (
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(event) =>
              handleToggleRow(typedRow.id, event.target.checked)
            }
            className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
          />
        );
      },
    },
    { key: "empCode", label: "Emp Code", bold: true },
    { key: "empName", label: "Emp Name", bold: true },
    { key: "branch", label: "Branch", bold: true },
    { key: "department", label: "Department", bold: true },
    { key: "designation", label: "Designation", bold: true },
  ];

  return (
    <div className="text-slate-900 dark:text-white bg-white dark:bg-slate-900 transition-colors duration-300">
      <BreadCrumb BreadCrumbList={breadCrumbList} />

      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
            <label className="inline-flex items-center gap-2 text-slate-700 dark:text-slate-200 whitespace-nowrap">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={(event) =>
                  handleSelectAllChange(event.target.checked)
                }
                className="h-5 w-5 rounded border-slate-300 text-primary focus:ring-primary"
              />
              <span className="text-md whitespace-nowrap">Select All</span>
            </label>

            <input
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              max={toDate || undefined}
              className="w-md sm:w-64 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2.5 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
            />

            <input
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              min={fromDate || undefined}
              className="w-md sm:w-64 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2.5 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
            />

            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md px-6 py-2.5 text-sm font-medium  border-slate-300 dark:border-slate-600 text-white bg-blue-700 hover:bg-blue-800  transition-colors"
            >
              Generate
            </button>
          </div>
        </div>

        <DataTable 
         data={rows}
          columns={tableColumns}
           loading={loading} />
      </div>
    </div>
  );
};

export default GeneratePayrollRun;
